import { useQuery } from '@tanstack/react-query';
import { fetcher } from 'src/api/fetcher';
import { API_RESOURCES, PAGINATION_IGNORE_DEFAULT_LIMIT } from 'src/utils/constants';

/**
 * Hook to fetch stock actions for a run
 * Used for badge calculations and initial stock action data
 */
export const useStockActionsForRun = (runUri, options = {}) => {
  return useQuery({
    queryKey: ['stock-actions', runUri],
    queryFn: () =>
      fetcher(API_RESOURCES.MATERIAL_STOCK_ACTION, {
        params: {
          'filter[run]': runUri,
          'page[limit]': PAGINATION_IGNORE_DEFAULT_LIMIT,
        },
      }),
    enabled: !!runUri,
    select: data => data.resources || [],
    staleTime: 0, // Always refetch on mount
    refetchOnMount: 'always', // Force refetch every time component mounts
    ...options,
  });
};

/**
 * Hook to fetch stock action relationships for a piece
 * Used to calculate group quantities and individual assignments
 */
export const useStockActionRelationshipsForPiece = (pieceUri, runUri, options = {}) => {
  return useQuery({
    queryKey: ['stock-action-relationships', pieceUri, runUri],
    queryFn: () =>
      fetcher(API_RESOURCES.MATERIAL_STOCK_ACTION_RELATIONSHIP, {
        params: {
          'filter[resource]': pieceUri,
          'filter[run]': runUri,
          'filter[relationship_type]': 'destination',
          'page[limit]': PAGINATION_IGNORE_DEFAULT_LIMIT,
        },
      }),
    enabled: !!pieceUri && !!runUri,
    select: data => data.resources || [],
    staleTime: 0, // Always refetch on mount
    refetchOnMount: 'always', // Force refetch every time component mounts
    ...options,
  });
};

/**
 * Hook to fetch stock actions filtered by run and source stock
 * Used for detailed stock assignment views
 */
export const useStockActionsByRunAndStock = (runUri, sourceStockUri, options = {}) => {
  return useQuery({
    queryKey: ['stock-actions', runUri, sourceStockUri],
    queryFn: () =>
      fetcher(API_RESOURCES.MATERIAL_STOCK_ACTION, {
        params: {
          'filter[run]': runUri,
          'filter[source_stock]': sourceStockUri,
          'page[limit]': PAGINATION_IGNORE_DEFAULT_LIMIT,
        },
      }),
    enabled: !!runUri && !!sourceStockUri,
    select: data => data.resources || [],
    staleTime: 30000,
    ...options,
  });
};
